import { forwardRef, useId, useImperativeHandle, useRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type I18nLabel = Record<Language, string>;

export interface FormRadioProps extends Omit<ComponentPropsWithoutRef<'input'>, 'type'> {
    label?: ReactNode;
    i18nLabel?: I18nLabel | string;
    wrapperClassName?: string;
    variant?: 'inline' | 'pill';
}

const FormRadio = forwardRef<HTMLInputElement, FormRadioProps>(function FormRadio(
    { label, i18nLabel, wrapperClassName, id, name, className, variant = 'inline', ...rest },
    ref,
) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(ref, () => inputRef.current!);
    const lang = useLanguage();
    const displayLabel = (typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang]) || label;
    const generatedId = useId();
    const controlId = id || generatedId;
    const isChecked = Boolean((rest as { checked?: boolean }).checked);
    const isDisabled = Boolean((rest as { disabled?: boolean }).disabled);

    const baseWrapper = 'flex items-center cursor-pointer';
    const pillWrapper = 'gap-3 rounded-md px-3 py-2 transition focus-within:ring-2 focus-within:ring-primary/25';
    const pillState = isChecked ? 'border border-slate-300 bg-slate-50' : 'border border-transparent hover:bg-slate-50';
    const wrapperClasses = variant === 'pill' ? `${baseWrapper} ${pillWrapper} ${pillState}` : baseWrapper;

    const labelExtra = variant === 'pill' ? 'ml-0 flex-1' : undefined;
    const inputClasses = twMerge([
        'h-4 w-4 cursor-pointer border-slate-300 text-primary shadow-sm transition focus:ring-4 focus:ring-primary/20 focus:ring-offset-0',
        variant === 'pill' ? 'mt-px' : undefined,
        className,
    ]);
    const labelClasses = twMerge([
        'cursor-pointer select-none text-sm text-slate-800 dark:text-slate-100',
        variant === 'pill' ? 'ml-0 flex-1' : 'ml-2',
        labelExtra,
    ]);

    const handleWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isDisabled) return;
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'LABEL') return;
        inputRef.current?.click();
    };

    return (
        <div className={twMerge([wrapperClasses, wrapperClassName])} onClick={handleWrapperClick}>
            <input ref={inputRef} id={controlId} name={name} type="radio" className={inputClasses} {...rest} />
            {displayLabel ? <label htmlFor={controlId} className={labelClasses}>{displayLabel}</label> : null}
        </div>
    );
});

export default FormRadio;
